import { useState } from 'react'
import type { Product } from '../types'
import { formatCurrency } from '../lib/utils'
import { useCartStore } from '../stores/cartStore'

interface ProductDetailsProps {
  product: Product
  onClose: () => void
}

function ProductDetails({ product, onClose }: ProductDetailsProps) {
  const [quantity, setQuantity] = useState(1)
  const addItem = useCartStore((state) => state.addItem)

  const handleAddToCart = () => {
    addItem(product, quantity)
    onClose()
  }

  const isOutOfStock = product.quantity <= 0
  const maxQuantity = Math.min(product.quantity, 99)

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Detail Produk</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
              {product.imageUrl ? (
                <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h3>
              {product.category && (
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full mb-4">
                  {product.category}
                </span>
              )}
              <div className="mb-4">
                <span className="text-3xl font-bold text-primary font-mono">
                  {formatCurrency(product.price)}
                </span>
              </div>
              {product.sku && (
                <p className="text-sm text-gray-500 mb-4">SKU: {product.sku}</p>
              )}
              {product.description && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-2">Deskripsi</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{product.description}</p>
                </div>
              )}
              <div className="mb-6">
                <h4 className="font-semibold mb-2">Ketersediaan</h4>
                {isOutOfStock ? (
                  <span className="text-error font-medium">Stok Habis</span>
                ) : (
                  <span className="text-success font-medium">Tersedia ({product.quantity} item)</span>
                )}
              </div>
              {!isOutOfStock && (
                <div className="space-y-4">
                  <div>
                    <label className="block font-semibold mb-2">Jumlah</label>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        max={maxQuantity}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.min(maxQuantity, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="w-20 text-center input-field"
                      />
                      <button
                        onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                        className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button onClick={handleAddToCart} className="btn-primary w-full py-3">
                    Tambah ke Keranjang
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetails
